import { reaction } from "mobx";
import { Properties } from "../forms/ComponentsMap";
import { IChartState, IExternalLayersState } from "../models/ChartModels";
import { GraticuleState } from "../models/GraticuleState";
import { MapControlsState } from "../models/MapControlsState";
import { UnitsState } from "../models/UnitsState";
import { IStateStore } from "./StateStore";

export interface LocalStorageStores {
    chart: IChartState;
    graticule: GraticuleState;
    units: UnitsState;
    external: IExternalLayersState;
    mapControls: MapControlsState
}

export type LocalStorageKey = keyof LocalStorageStores;

export const BASE_LOCAL_STORAGE_KEY = 'map-settings-'
export const AUTO_SAVE_DELAY = 1000;

export class MapStoreLocalStorage<K extends LocalStorageKey, S extends Properties> {

    private timeout: number | undefined;
    private get localStorageKey() {
        return BASE_LOCAL_STORAGE_KEY + this.key;
    }

    constructor(
        private key: K,
        private store: IStateStore<S>,
        private autosave: (key: K) => boolean,
        private filter: (state: S) => Partial<S> = state => state
    ) {
        reaction(() => this.store.getState, (value) => {            
            window.clearTimeout(this.timeout);
            if (this.autosave(this.key)) {
                this.timeout = window.setTimeout(() => {
                    this.toLocalStorage(value);
                }, AUTO_SAVE_DELAY);
            }            
        })
    }    

    public load() {      
        const value = localStorage.getItem(this.localStorageKey);
        if (value) {
            try {
                const result = JSON.parse(value) as Partial<S>;
                this.store.setState(result);
            }
            catch (e) {
                console.log('*** Failed to get key', { key: this.localStorageKey, value });
            }
        }        
    }

    public save() {
        window.clearTimeout(this.timeout);
        this.toLocalStorage(this.store.getState);
    }    

    private toLocalStorage(state: S) {       
        const value = JSON.stringify(this.filter(state));
        localStorage.setItem(this.localStorageKey, value);
    }
}